import React, { Component } from "react";
import { Paper, Typography } from "@material-ui/core";
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import * as deviceFaultReducer from '../reducer';

class SystemDeviceFaultsHistogram extends Component {
    constructor(props) {
        super(props);
        this.state = {
            chartOptions: {
                chart: {
                    type: 'column',
                    backgroundColor: null // Use default white background
                },
                title: {
                    text: ''
                },
                xAxis: {
                    categories: [],
                    crosshair: true
                },
                yAxis: {
                    min: 0,
                    title: {
                        text: 'Number of Faults'
                    },
                    stackLabels: {
                        enabled: true,
                        style: {
                            fontWeight: 'bold'
                        }
                    },
                    allowDecimals: false, // Ensures no decimal places in yAxis labels
                    tickInterval: 1, // Forces yAxis ticks to be integers
                },
                tooltip: {
                    shared: true,
                    useHTML: true,
                    headerFormat: '<b>{point.key}</b><br/>',
                    pointFormat: '{series.name}: {point.y}<br/>',
                    footerFormat: '<b>Total: {point.total}</b>'
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        dataLabels: {
                            enabled: true,
                            formatter: function () {
                                return this.y > 0 ? this.y : "";
                            }
                        }
                    }
                },
                series: [],
                credits: {
                    enabled: false
                },
                exporting: {
                    enabled: true,
                    buttons: {
                    contextButton: {
                        menuItems: ['viewFullscreen'] // only this item will show
                    }
                    }
                }
            },
            dateText: '' 
        };
    }

    componentDidMount() {
        this.processData(this.props.stationDeviceFaultData);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.stationDeviceFaultData !== this.props.stationDeviceFaultData) {
            this.processData(this.props.stationDeviceFaultData);
        }
    }

    componentWillUnmount() {
        this.props.stationDeviceFaultData.deviceFaults = []; // Clear the device faults data
        this.props.stationDeviceFaultData.deviceFaultsCount = []; // Clear the device faults data
    }

    processData = (data) => {
        if (!data) return;
    
        const dataByDevice = {};
        const timestamps = [];
        const displayLabels = [];
        const fullDates = [];
    
        const deviceColors = {
            'inverter1': '#7cb5ec', // Blue
            'inverter2': '#434348', // Dark Gray
            'inverter3': '#90ed7d',  // Light Green
            'inverter4': '#f7a35c',  // Orange
            'inverter5': '#8085e9',  // Light Purple
            'inverter6': '#f15c80',  // Pink
            'inverter7': '#e4d354',  // Yellow
            'inverter8': '#2b908f',  // Teal
            'inverter9': '#f45b5b'   // Red
        };
        let totalCount = 0;
    
        if (data.deviceFaultsCount && data.deviceFaultsCount.length > 0) {
            data.deviceFaultsCount.forEach(fault => {
                let date = new Date(fault.timestamp);
                let timeStr = date.toLocaleString('default', { month: 'short', year: 'numeric' });
                let displayStr = date.toLocaleString('default', { month: 'short' });
    
                fullDates.push(date); // Add to fullDates for dateText
    
                if (timestamps.indexOf(timeStr) === -1) {
                    timestamps.push(timeStr);
                    displayLabels.push(displayStr);
                }
    
                if (fault.deviceName) {
                    if (!dataByDevice[fault.deviceName]) {
                        dataByDevice[fault.deviceName] = {};
                    }
                    dataByDevice[fault.deviceName][timeStr] = parseInt(fault.distinctErrorCount) || 0;
                    totalCount += fault.distinctErrorCount;
                }
            });
        } else {
            data.deviceFaults.forEach(fault => {
                let date = new Date(fault.timestamp);
                let timeStr;
    
                if (this.props.selectedView === 'day') {
                    // Keep the full date for dateText and internal processing
                    fullDates.push(date);
                    // If the view is 'day', show only hours in AM/PM format
                    timeStr = date.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit', hour12: true });
                } else if (this.props.selectedView === 'month') {
                    // Keep the full date for dateText and internal processing
                    fullDates.push(date);
                    // Show day of the month for month view
                    timeStr = date.getDate();
                } else if (this.props.selectedView === 'range') {
                    // Format the date as YYYY/MM/DD for range view
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    timeStr = `${year}/${month}/${day}`;
                    fullDates.push(date);
                } else {
                    // Keep the full date for dateText and internal processing
                    fullDates.push(date);
                    // Otherwise, show only the date
                    timeStr = date.toLocaleDateString();
                }
    
                if (timestamps.indexOf(timeStr) === -1) {
                    timestamps.push(timeStr);
                    displayLabels.push(timeStr);
                }
    
                if (fault.deviceName) {
                    if (!dataByDevice[fault.deviceName]) {
                        dataByDevice[fault.deviceName] = {};
                    }
                    dataByDevice[fault.deviceName][timeStr] = fault.distinctErrorCount || 0;
                    totalCount += fault.distinctErrorCount;
                }
            });
        }
    
        const series = Object.keys(dataByDevice).map(deviceName => {
            const data = timestamps.map(time => dataByDevice[deviceName][time] || 0);
            return {
                name: deviceName,
                data,
                color: deviceColors[deviceName]
            };
        });
    
        let dateText = '';
        if (fullDates.length > 0) {
            const firstDate = fullDates[0];
            const lastDate = fullDates[fullDates.length - 1];
            const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}/${month}/${day}`;
            };
            switch (this.props.selectedView) {
                case 'day':
                    dateText = formatDate(firstDate); // Show the selected date
                    break;
                case 'month':
                    dateText = formatDate(firstDate).slice(0, 7); // Show year and month only
                    break;
                case 'year':
                    dateText = firstDate.getFullYear().toString();
                    break;
                case 'range':
                    dateText = `${formatDate(firstDate)} - ${formatDate(lastDate)}`;
                    break;
                default:
                    dateText = '';
            }
        }
    
        this.setState({
            chartOptions: {
                ...this.state.chartOptions,
                title: {
                    text: `Total Fault Count: ${totalCount}`, 
                    style: {
                        fontSize: '1rem', 
                        fontWeight: '400',
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', 
                    }
                },
                xAxis: {
                    ...this.state.chartOptions.xAxis,
                    categories: displayLabels
                },
                series: series
            },
            dateText: dateText,
            hasData: series.some(serie => serie.data.some(value => value > 0)) // Check for non-zero values in series data
        });
    }
    

    render() {
        const { chartOptions, dateText, hasData } = this.state;
        const { loading } = this.props.stationDeviceFaultData; // Assuming you have a loading prop in stationDeviceFaultData
    
        return (
            <Paper className="SmartOMInfo">
                <Typography className="SmartOMTitle" variant="h6" color="primary">
                    Fault Distribution Histogram
                </Typography>
                {dateText && (
                    <Typography variant="subtitle1" style={{ margin: '5px' }}>
                        {dateText}
                    </Typography>
                )}
                <div style={{ overflowX: 'auto' }}>
                    {!loading && hasData ? (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={chartOptions}
                        />
                    ) : (
                        <Typography variant="subtitle1" style={{ margin: '20px' }}>
                            No data available. Please submit to see the histogram.
                        </Typography>
                    )}
                </div>
            </Paper>
        );
    }
    
}

const mapStateToProps = (state) => ({
    stationDeviceFaultData: state.systemDeviceFaults.queryDeviceFaultData,
    selectedView: deviceFaultReducer.getSelectedView(state)
});

export default connect(mapStateToProps)(SystemDeviceFaultsHistogram);
